import accounting from "accounting"

import StatusCell from "@/components/grid/StatusCell"
import { CreatorCell, CreatorCellSkeleton, PitchStatusBadgeSkeleton, type TableColumn } from "@/components/tableKit"
import { TextLink } from "@/components/typography"
import { Skeleton } from "@/components/ui/skeleton"
import { getCurrencySymbol } from "@/shared/currency"
import type { Booking, Currency, Talent } from "@/shared/types"

export type CampaignBookingRow = Booking & {
    talent?: Talent
}

export type BookingsColumnsOptions = {
    currency?: Currency
}

/** Bookings tab columns: creator, manager, status, price, links, and boost code. */
export function bookingsColumns({
    currency = "USD",
}: BookingsColumnsOptions = {}): TableColumn<CampaignBookingRow>[] {
    return [
        {
            id: "creator",
            header: "Creator",
            cell: (row) => <CreatorCell talent={row.talent} />,
            skeleton: <CreatorCellSkeleton />,
        },
        {
            id: "manager",
            header: "Manager",
            skeleton: (
                <div>
                    <Skeleton className="mb-1 h-4 w-28" />
                    <Skeleton className="h-4 w-36" />
                </div>
            ),
            cell: (row) => {
                const manager = row.talent?.managers?.[0]
                if (!manager) {
                    return null
                }
                return (
                    <div>
                        <div>
                            {manager.firstName} {manager.lastName}
                        </div>
                        {manager.email ? (
                            <div>
                                <TextLink href={`mailto:${manager.email}`}>{manager.email}</TextLink>
                            </div>
                        ) : null}
                    </div>
                )
            },
        },
        {
            id: "status",
            header: "Status",
            cell: (row) => <StatusCell value={row.status} />,
            skeleton: <PitchStatusBadgeSkeleton />,
        },
        {
            id: "price",
            header: "Price",
            skeleton: <Skeleton className="h-4 w-16" />,
            cell: (row) =>
                row.price != null
                    ? accounting.formatMoney(
                        row.price,
                        getCurrencySymbol(row.currency || currency),
                        2,
                        ",",
                        ".",
                    )
                    : null,
        },
        {
            id: "videoLink",
            header: "Video Link",
            skeleton: <Skeleton className="h-4 w-32" />,
            cell: (row) =>
                row.videoLink ? (
                    <TextLink href={row.videoLink} target="_blank" rel="noreferrer">
                        {row.videoLink}
                    </TextLink>
                ) : null,
        },
        {
            id: "boostCode",
            header: "Boost Code",
            skeleton: <Skeleton className="h-4 w-20" />,
            cell: (row) => row.boostCode || null,
        },
        {
            id: "invoiceLink",
            header: "Invoice Link",
            skeleton: <Skeleton className="h-4 w-32" />,
            cell: (row) =>
                row.invoiceLink ? (
                    <TextLink href={row.invoiceLink} target="_blank" rel="noreferrer">
                        {row.invoiceLink}
                    </TextLink>
                ) : null,
        },
    ]
}
